// Container do detalhe de Configuração de um white label (§4.3):
// page header rico (identificação do WL + canais/providers + mailFrom
// copiável + contadores-resumo) e eventos do contrato em cards de
// entidade agrupados por origem (Pedido / Assinatura / Ambos), cada
// um com switch otimista, badges e agendamentos. Eventos do catálogo
// (21 do enum) que não vierem no payload aparecem em "Não
// configurados", com CTA acionável de criação da configuração padrão
// (POST …/configurations/default) — que vira o hero do empty state
// quando o WL não tem NENHUM evento configurado.
import {
  CalendarClockIcon,
  CheckCircle2Icon,
  CircleDashedIcon,
  LayersIcon,
  PencilIcon,
  PlugIcon,
  RefreshCcwIcon,
  ShoppingCartIcon,
  SparklesIcon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useParams } from "react-router-dom"

import { InfoItem } from "@/components/shared/detail-components"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import type {
  NotificationOriginTransactionType,
  NotificationWhiteLabelEventType,
} from "@/types/notification-center"

import { useCreateDefaultConfiguration } from "../configuration-mutations"
import { NOTIFICATION_EVENTS } from "../constants"
import { withEventRowKeys } from "../event-transaction-type"
import { useNotificationCenterWhiteLabelEvents } from "../hooks"
import { WhiteLabelEventRow } from "./white-label-event-row"
import { WhiteLabelMailFromDialog } from "./white-label-mailfrom-dialog"

const detailPrefix =
  "pages.notificationCenter.configurationsDetailPage"

// Ordem de exibição dos grupos por origem (§4.3).
const ORIGIN_GROUPS = [
  "ORDER",
  "SUBSCRIPTION",
  "BOTH",
] as const
type OriginGroup = (typeof ORIGIN_GROUPS)[number]

// Ícone + cor do header de cada grupo (paleta semântica Pay2B).
const ORIGIN_GROUP_ICONS: Record<
  OriginGroup,
  { Icon: typeof ShoppingCartIcon; className: string }
> = {
  ORDER: {
    Icon: ShoppingCartIcon,
    className: "text-blue-500",
  },
  SUBSCRIPTION: {
    Icon: RefreshCcwIcon,
    className: "text-violet-500",
  },
  BOTH: { Icon: LayersIcon, className: "text-sky-500" },
}

// Grupo do evento a partir do `origin` do contrato.
const resolveOriginGroup = (
  origin: NotificationOriginTransactionType[]
): OriginGroup => (origin.length === 2 ? "BOTH" : origin[0])

// Skeleton fiel ao layout novo: card de overview (identificação +
// contadores) e grupos com título + cards de evento.
const EventsSkeleton = () => (
  <div
    data-testid="wl-events-skeleton"
    className="space-y-8"
  >
    <Skeleton className="h-44 w-full rounded-xl" />
    {Array.from({ length: 2 }).map((_, group) => (
      <div
        key={`wl-events-skeleton-group-${group}`}
        className="space-y-3"
      >
        <Skeleton className="h-5 w-44" />
        {Array.from({ length: 3 }).map((_, row) => (
          <Skeleton
            key={`wl-events-skeleton-${group}-${row}`}
            className="h-[72px] w-full rounded-xl"
          />
        ))}
      </div>
    ))}
  </div>
)

// Contador-resumo do header (número em tabular-nums + rótulo).
const SummaryStat = ({
  icon,
  iconClassName,
  count,
  label,
  testId,
}: {
  icon: React.ReactNode
  iconClassName: string
  count: number
  label: string
  testId: string
}) => (
  <div
    data-testid={testId}
    className="flex items-center gap-3 px-4 py-3"
  >
    <div
      className={cn(
        "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
        iconClassName
      )}
    >
      {icon}
    </div>
    <div className="flex flex-col gap-0.5">
      <span className="text-lg font-bold tabular-nums leading-none">
        {count}
      </span>
      <span className="text-xs text-muted-foreground">
        {label}
      </span>
    </div>
  </div>
)

// Header de seção de grupo: ícone + título + badge de contagem.
const GroupSectionHeader = ({
  icon,
  title,
  count,
  muted,
}: {
  icon: React.ReactNode
  title: string
  count: number
  muted?: boolean
}) => (
  <div className="flex items-center gap-2">
    {icon}
    <h2
      className={cn(
        "text-base font-semibold",
        muted && "text-muted-foreground"
      )}
    >
      {title}
    </h2>
    <Badge
      variant="secondary"
      className="h-5 px-1.5 text-xs tabular-nums"
    >
      {count}
    </Badge>
  </div>
)

export const WhiteLabelConfigurations = () => {
  const { t } = useTranslation()
  const { whiteLabelId } = useParams()
  const [
    isDefaultConfigDialogOpen,
    setIsDefaultConfigDialogOpen,
  ] = useState(false)
  const [isMailFromDialogOpen, setIsMailFromDialogOpen] =
    useState(false)
  const { data, isLoading, isError, refetch } =
    useNotificationCenterWhiteLabelEvents(
      whiteLabelId as string
    )
  const createDefaultConfiguration =
    useCreateDefaultConfiguration(whiteLabelId as string)

  const groupedEvents = ORIGIN_GROUPS.map((group) => ({
    group,
    events:
      data?.events.filter(
        (event: NotificationWhiteLabelEventType) =>
          resolveOriginGroup(event.origin) === group
      ) ?? [],
  })).filter(({ events }) => events.length > 0)

  // Eventos do catálogo (enum) ausentes no payload do WL.
  const configuredEventNames = new Set(
    data?.events.map((event) => event.name)
  )
  const notConfiguredEvents = data
    ? NOTIFICATION_EVENTS.filter(
        (event) => !configuredEventNames.has(event.value)
      )
    : []

  const activeChannels =
    data?.channels.filter((channel) => channel.active) ?? []

  // Contadores-resumo do header.
  const activeEventsCount =
    data?.events.filter((event) => event.enabled).length ??
    0
  const scheduledEventsCount =
    data?.events.filter(
      (event) => event.schedulers.length > 0
    ).length ?? 0

  const hasConfiguredEvents = (data?.events.length ?? 0) > 0

  return (
    <div className="space-y-6 pb-10">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight">
          {t(`${detailPrefix}.title`)}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(`${detailPrefix}.subtitle`)}
        </p>
      </header>

      {isLoading && <EventsSkeleton />}

      {!isLoading && isError && (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-6">
          <p className="text-center text-sm text-red-700">
            {t(`${detailPrefix}.loadError`)}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => refetch()}
          >
            {t(`${detailPrefix}.retry`)}
          </Button>
        </div>
      )}

      {!isLoading && !isError && data && (
        <div className="space-y-8">
          {/* Overview do WL: identificação + canais + contadores. */}
          <section
            aria-label={t(`${detailPrefix}.title`)}
            className="rounded-xl border bg-card shadow-sm"
          >
            <div className="flex flex-col gap-4 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold">
                  {t(`${detailPrefix}.channelsTitle`)}
                </span>
                {/* Entrada da tela de Provedores do WL. */}
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="ml-auto shrink-0"
                >
                  <Link
                    to={`/notification-center/configurations/${data.whiteLabelId}/providers`}
                  >
                    <PlugIcon
                      className="mr-1.5 size-4"
                      aria-hidden="true"
                    />
                    {t(`${detailPrefix}.manageProviders`)}
                  </Link>
                </Button>
                {data.channels.length === 0 && (
                  <span className="text-sm text-muted-foreground">
                    {t(`${detailPrefix}.noChannels`)}
                  </span>
                )}
                {data.channels.map(
                  ({ channel, provider, active }) => (
                    <Badge
                      key={`${channel}-${provider}`}
                      variant="outline"
                      className={cn(
                        "rounded-full border font-medium",
                        active
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-zinc-200 bg-zinc-50 text-zinc-500"
                      )}
                    >
                      {channel} · {provider}
                      {!active &&
                        ` (${t(`${detailPrefix}.channelInactive`)})`}
                    </Badge>
                  )
                )}
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-3">
                <InfoItem
                  label={t(
                    `${detailPrefix}.whiteLabelIdLabel`
                  )}
                  value={data.whiteLabelId}
                  copyable
                  truncate
                />
                <div className="flex items-end gap-1">
                  <InfoItem
                    label={t(`${detailPrefix}.mailFrom`)}
                    value={data.mailFrom}
                    copyable
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7 shrink-0"
                    aria-label={t(
                      `${detailPrefix}.mailFromDialog.editAction`
                    )}
                    onClick={() =>
                      setIsMailFromDialogOpen(true)
                    }
                  >
                    <PencilIcon
                      className="size-3.5"
                      aria-hidden="true"
                    />
                  </Button>
                </div>
              </div>
            </div>
            <Separator />
            <div className="grid grid-cols-1 divide-y sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              <SummaryStat
                testId="wl-summary-active"
                icon={
                  <CheckCircle2Icon
                    className="size-4"
                    aria-hidden="true"
                  />
                }
                iconClassName="bg-emerald-50 text-emerald-600"
                count={activeEventsCount}
                label={t(`${detailPrefix}.summaryActive`)}
              />
              <SummaryStat
                testId="wl-summary-schedulers"
                icon={
                  <CalendarClockIcon
                    className="size-4"
                    aria-hidden="true"
                  />
                }
                iconClassName="bg-amber-50 text-amber-600"
                count={scheduledEventsCount}
                label={t(
                  `${detailPrefix}.summaryWithSchedulers`
                )}
              />
              <SummaryStat
                testId="wl-summary-not-configured"
                icon={
                  <CircleDashedIcon
                    className="size-4"
                    aria-hidden="true"
                  />
                }
                iconClassName="bg-zinc-100 text-zinc-500"
                count={notConfiguredEvents.length}
                label={t(
                  `${detailPrefix}.summaryNotConfigured`
                )}
              />
            </div>
          </section>

          {hasConfiguredEvents ? (
            groupedEvents.map(({ group, events }) => {
              const { Icon, className } =
                ORIGIN_GROUP_ICONS[group]
              return (
                <section key={group} className="space-y-3">
                  <GroupSectionHeader
                    icon={
                      <Icon
                        className={cn("size-4", className)}
                        aria-hidden="true"
                      />
                    }
                    title={t(
                      `${detailPrefix}.groups.${group}`
                    )}
                    count={events.length}
                  />
                  <Separator />
                  <ul className="flex flex-col gap-3">
                    {/* Chave por name + linha: o mesmo evento
                        existe para pedido E assinatura. */}
                    {withEventRowKeys(events).map(
                      ({ key, event }) => (
                        <WhiteLabelEventRow
                          key={key}
                          whiteLabelId={data.whiteLabelId}
                          event={event}
                          activeChannels={activeChannels}
                        />
                      )
                    )}
                  </ul>
                </section>
              )
            })
          ) : (
            /* Empty state hero: WL sem NENHUM evento — o CTA de
               configuração padrão é a ação principal da página. */
            <div
              data-testid="wl-events-empty"
              className="flex flex-col items-center gap-4 rounded-xl border border-dashed px-6 py-14 text-center"
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <SparklesIcon
                  className="size-7 text-primary"
                  aria-hidden="true"
                />
              </div>
              <div className="space-y-1">
                <p className="text-base font-semibold">
                  {t(`${detailPrefix}.emptyHeroTitle`)}
                </p>
                <p className="mx-auto max-w-md text-sm text-muted-foreground">
                  {t(
                    `${detailPrefix}.emptyHeroDescription`
                  )}
                </p>
              </div>
              <Button
                type="button"
                size="lg"
                disabled={
                  createDefaultConfiguration.isPending
                }
                onClick={() =>
                  setIsDefaultConfigDialogOpen(true)
                }
              >
                <SparklesIcon
                  className="mr-1.5 size-4"
                  aria-hidden="true"
                />
                {t(
                  `${detailPrefix}.defaultConfig.ctaAction`
                )}
              </Button>
            </div>
          )}

          {/* Catálogo sem configuração no WL + CTA acionável. */}
          {notConfiguredEvents.length > 0 && (
            <section className="space-y-3">
              <GroupSectionHeader
                icon={
                  <CircleDashedIcon
                    className="size-4 text-zinc-400"
                    aria-hidden="true"
                  />
                }
                title={t(
                  `${detailPrefix}.groups.NOT_CONFIGURED`
                )}
                count={notConfiguredEvents.length}
                muted
              />
              <Separator />
              <p className="text-sm text-muted-foreground">
                {t(`${detailPrefix}.notConfiguredHint`)}
              </p>
              {/* CTA destacado — some quando o hero já é o CTA. */}
              {hasConfiguredEvents && (
                <div className="flex flex-col gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <SparklesIcon
                        className="size-5 text-primary"
                        aria-hidden="true"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">
                        {t(
                          `${detailPrefix}.defaultConfig.ctaTitle`
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t(
                          `${detailPrefix}.defaultConfig.ctaDescription`
                        )}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    className="shrink-0"
                    disabled={
                      createDefaultConfiguration.isPending
                    }
                    onClick={() =>
                      setIsDefaultConfigDialogOpen(true)
                    }
                  >
                    <SparklesIcon
                      className="mr-1.5 size-4"
                      aria-hidden="true"
                    />
                    {t(
                      `${detailPrefix}.defaultConfig.ctaAction`
                    )}
                  </Button>
                </div>
              )}
              <ul className="flex flex-col gap-2">
                {notConfiguredEvents.map((event) => (
                  <li
                    key={event.value}
                    className="flex items-center justify-between gap-2 rounded-xl border border-dashed bg-muted/40 p-3"
                  >
                    <span className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CircleDashedIcon
                        className="size-4 shrink-0 text-zinc-400"
                        aria-hidden="true"
                      />
                      {event.label}
                    </span>
                    <Badge
                      variant="outline"
                      className="rounded-full border-zinc-200 bg-zinc-50 font-medium text-zinc-500"
                    >
                      {t(
                        `${detailPrefix}.notConfiguredBadge`
                      )}
                    </Badge>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {/* Edição do remetente (mailFrom). */}
      {data && (
        <WhiteLabelMailFromDialog
          open={isMailFromDialogOpen}
          onOpenChange={setIsMailFromDialogOpen}
          whiteLabelId={data.whiteLabelId}
          currentMailFrom={data.mailFrom}
        />
      )}

      {/* Confirmação do POST da configuração padrão. */}
      <AlertDialog
        open={isDefaultConfigDialogOpen}
        onOpenChange={setIsDefaultConfigDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                `${detailPrefix}.defaultConfig.dialogTitle`
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                `${detailPrefix}.defaultConfig.dialogDescription`
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(`${detailPrefix}.defaultConfig.cancel`)}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                createDefaultConfiguration.mutate()
              }
            >
              {t(`${detailPrefix}.defaultConfig.confirm`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
